export type BeatLineInterventionContext = {
  state: Record<string, any>;
  online?: Record<string, any>;
  songMetaCache?: Record<string, any>;
  ajaxPost?: (action: string, payload?: Record<string, any>) => Promise<any>;
  beatlineBuildPlaylistParams?: (ids: unknown[]) => Record<string, any>;
  currentPlaylistId?: string;
  beatlineLang?: string;
  t?: (key: string, fallback?: string) => string;
  tf?: (key: string, vars?: Record<string, any>, fallback?: string) => string;
  createCardHTML?: (card: Record<string, any>, revealed: boolean, opts?: Record<string, any>) => string;
  createPendingCardHTML?: (id: unknown) => string;
  updateUI?: (skipTimelineRebuild?: boolean) => void;
  fetchSongMeta?: (ids: unknown[]) => Promise<any>;
  revealCardElement?: (el: Element, meta: Record<string, any>) => void;
  finalizeTurn?: (playerId: number, valid: boolean) => void;
  showCenterIntervention?: (opts?: Record<string, any>) => void;
  showCenterInterventionPickPlayer?: (opts?: Record<string, any>) => void;
  showCenterInterventionActive?: (opts?: Record<string, any>) => void;
  hideCenterIntervention?: () => void;
  showBonusModal?: (headerText: string, subMsg: string, status: string, opts?: Record<string, any>) => void;
  beatlineInterventionActiveCorrectText?: (targetP: number, actorP: number) => string;
  beatlineInterventionStolenText?: (actorP: number, targetP: number) => string;
  beatlineInterventionBothWrongText?: (actorP: number) => string;
  triggerStealEffect?: () => void;
  triggerWrongEffect?: () => void;
  confetti?: (...args: any[]) => void;
  stopMusic?: () => void;
};

export type BeatLineInterventionResult = { handled: boolean; reason?: string };

function safeCall(fn: unknown, ...args: any[]): void {
  if (typeof fn !== 'function') return;
  try { fn(...args); } catch (_) {}
}

function getT(ctx: BeatLineInterventionContext) {
  return typeof ctx.t === 'function' ? ctx.t : ((_key: string, fallback = _key) => fallback);
}

function getTf(ctx: BeatLineInterventionContext) {
  return typeof ctx.tf === 'function' ? ctx.tf : ((key: string, vars: Record<string, any> = {}) => {
    if (key === 'turn_player_choose_pos') return `SPELER ${vars.p || ''}: KIES POSITIE`;
    return key;
  });
}


function centerEls(): any | null {
  const overlay = document.getElementById('center-intervention') as HTMLElement | null;
  const grid = document.getElementById('center-intervention-grid') as HTMLElement | null;
  const pickWrap = document.getElementById('center-intervention-pick') as HTMLElement | null;
  const noBtn = document.getElementById('center-intervention-no') as HTMLButtonElement | null;
  const yesBtn = document.getElementById('center-intervention-yes') as HTMLButtonElement | null;
  const backBtn = document.getElementById('center-intervention-back') as HTMLButtonElement | null;
  if (!overlay || !grid || !pickWrap || !noBtn || !yesBtn || !backBtn) return null;
  const titleEl = overlay.querySelector('[data-i18n="intervention_title"]') as HTMLElement | null;
  const questionEl = overlay.querySelector('[data-i18n="intervention_question"]') as HTMLElement | null;
  return { overlay, grid, pickWrap, noBtn, yesBtn, backBtn, titleEl, questionEl };
}

function openCenterOverlay(): void {
  const els = centerEls();
  if (!els) return;
  els.overlay.classList.add('is-open');
  els.overlay.style.display = '';
  els.overlay.setAttribute('aria-hidden', 'false');
}

function focusOutsideCenterOverlay(): void {
  const els = centerEls();
  if (!els) return;
  const active = document.activeElement as HTMLElement | null;
  if (active && els.overlay.contains(active)) {
    active.blur();
    const fallback = document.getElementById('btn-check') || document.getElementById('btn-next') || document.getElementById('beatline-app');
    if (fallback instanceof HTMLElement) fallback.focus?.({ preventScroll: true });
  }
}

function hideCenterOverlayFromTypeScript(): void {
  const els = centerEls();
  if (!els) return;
  focusOutsideCenterOverlay();
  els.pickWrap.hidden = true;
  els.pickWrap.innerHTML = '';
  els.backBtn.hidden = true;
  els.backBtn.onclick = null;
  els.noBtn.hidden = false;
  els.yesBtn.hidden = false;
  els.noBtn.onclick = null;
  els.yesBtn.onclick = null;
  els.grid.dataset.mode = 'decision';
  els.overlay.classList.remove('is-open');
  els.overlay.style.display = 'none';
  els.overlay.setAttribute('aria-hidden', 'true');
}

function showCenterDecisionFromTypeScript(opts: Record<string, any>, ctx: BeatLineInterventionContext): void {
  const els = centerEls();
  if (!els) return;
  const l = lang(ctx);
  els.grid.dataset.mode = 'decision';
  els.pickWrap.hidden = true;
  els.pickWrap.innerHTML = '';
  els.backBtn.hidden = true;
  els.backBtn.onclick = null;
  els.noBtn.hidden = false;
  els.yesBtn.hidden = false;
  els.noBtn.innerText = typeof opts.noText === 'string' ? opts.noText : (l === 'en' ? 'NO' : 'NEE');
  els.yesBtn.innerText = typeof opts.yesText === 'string' ? opts.yesText : (l === 'en' ? 'YES' : 'JA');
  els.noBtn.onclick = typeof opts.onNo === 'function' ? opts.onNo : null;
  els.yesBtn.onclick = typeof opts.onYes === 'function' ? opts.onYes : null;
  if (els.titleEl) els.titleEl.innerText = typeof opts.titleText === 'string' ? opts.titleText : '';
  if (els.questionEl && typeof opts.questionText === 'string') els.questionEl.innerText = opts.questionText;
  openCenterOverlay();
}

function showCenterPickFromTypeScript(opts: Record<string, any>, ctx: BeatLineInterventionContext): void {
  const els = centerEls();
  if (!els) return;
  const l = lang(ctx);
  const challengers = Array.isArray(opts.challengers) ? opts.challengers : [];
  els.grid.dataset.mode = 'pick';
  els.noBtn.hidden = true;
  els.yesBtn.hidden = true;
  els.pickWrap.hidden = false;
  els.pickWrap.innerHTML = '';
  challengers.forEach((pid: number) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'ci-btn ci-yes';
    b.innerText = l === 'en' ? `PLAYER ${pid}` : `SPELER ${pid}`;
    b.onclick = () => { if (typeof opts.onPick === 'function') opts.onPick(pid); };
    els.pickWrap.appendChild(b);
  });
  els.backBtn.hidden = false;
  els.backBtn.innerText = l === 'en' ? 'BACK' : 'TERUG';
  els.backBtn.onclick = typeof opts.onBack === 'function' ? opts.onBack : null;
  if (els.titleEl && typeof opts.titleText === 'string') els.titleEl.innerText = opts.titleText;
  if (els.questionEl) els.questionEl.innerText = typeof opts.questionText === 'string' ? opts.questionText : (l === 'en' ? 'WHO WILL INTERVENE?' : 'WIE GRIJPT IN?');
  openCenterOverlay();
}

function showCenterActiveFromTypeScript(opts: Record<string, any>, ctx: BeatLineInterventionContext): void {
  const els = centerEls();
  if (!els) return;
  const l = lang(ctx);
  const pid = parseInt(String(opts.challengerId || 0), 10) || 0;
  els.grid.dataset.mode = 'active';
  els.noBtn.hidden = true;
  els.yesBtn.hidden = true;
  els.pickWrap.hidden = false;
  els.pickWrap.innerHTML = '';
  const info = document.createElement('button');
  info.type = 'button';
  info.className = 'ci-btn ci-yes ci-selected';
  info.disabled = true;
  info.innerText = l === 'en' ? `INTERVENTION: PLAYER ${pid}` : `INTERVENTIE: SPELER ${pid}`;
  els.pickWrap.appendChild(info);
  els.backBtn.hidden = false;
  els.backBtn.innerText = l === 'en' ? 'BACK' : 'TERUG';
  els.backBtn.onclick = typeof opts.onBack === 'function' ? opts.onBack : null;
  if (els.questionEl) els.questionEl.innerText = l === 'en' ? 'POSITION SELECTING…' : 'POSITIE KIEZEN…';
  openCenterOverlay();
}

function callShowDecision(ctx: BeatLineInterventionContext, opts: Record<string, any>): void {
  if (typeof ctx.showCenterIntervention === 'function') ctx.showCenterIntervention(opts);
  else showCenterDecisionFromTypeScript(opts, ctx);
}

function callShowPick(ctx: BeatLineInterventionContext, opts: Record<string, any>): void {
  if (typeof ctx.showCenterInterventionPickPlayer === 'function') ctx.showCenterInterventionPickPlayer(opts);
  else showCenterPickFromTypeScript(opts, ctx);
}

function callShowActive(ctx: BeatLineInterventionContext, opts: Record<string, any>): void {
  if (typeof ctx.showCenterInterventionActive === 'function') ctx.showCenterInterventionActive(opts);
  else showCenterActiveFromTypeScript(opts, ctx);
}

function callHideCenter(ctx: BeatLineInterventionContext): void {
  if (typeof ctx.hideCenterIntervention === 'function') ctx.hideCenterIntervention();
  else hideCenterOverlayFromTypeScript();
}

function insertChallengePendingCard(zone: HTMLElement, id: string, ctx: BeatLineInterventionContext): void {
  const html = typeof ctx.createPendingCardHTML === 'function' ? ctx.createPendingCardHTML(id) : fallbackPendingCardHTML(id);
  const tpl = document.createElement('template');
  tpl.innerHTML = html.trim();
  const el = tpl.content.firstElementChild as HTMLElement | null;
  if (el) {
    // In de normale tijdlijn gebruikt .pending een outline bovenop de actieve-card border.
    // In interventie-modus oogt dat als een dubbele rand; de originele geplaatste kaart moet hier
    // visueel één actieve kaart blijven terwijl de plaatsknoppen eromheen verschijnen.
    el.classList.remove('pending');
    el.classList.add('intervention-pending-card');
    zone.appendChild(el);
  } else {
    zone.insertAdjacentHTML('beforeend', html);
  }
}

function lang(ctx: BeatLineInterventionContext): string {
  return String(ctx.beatlineLang || (window as any).beatlineLang || 'nl').toLowerCase();
}

function normalOfflineOnly(ctx: BeatLineInterventionContext): boolean {
  const state = ctx.state || {};
  const online = ctx.online || {};
  if (online && online.enabled) return false;
  return !(state.perfectOrder || state.timeAttack || state.survival || state.speedrun);
}

function player(ctx: BeatLineInterventionContext, slot: number): Record<string, any> {
  const key = `p${slot}`;
  const state = ctx.state;
  if (!state[key] || typeof state[key] !== 'object') state[key] = { score: 0, tokens: 0, timeline: [] };
  return state[key];
}

export function getOfflineChallengersFromTypeScript(ctx: BeatLineInterventionContext): number[] {
  const state = ctx.state || {};
  const challengers: number[] = [];
  const playerCount = parseInt(String(state.playerCount || 1), 10) || 1;
  const turn = parseInt(String(state.turn || 1), 10) || 1;
  if (playerCount > 1) {
    for (let i = 1; i <= playerCount; i += 1) {
      const p = player(ctx, i);
      if (i !== turn && (parseInt(String(p.tokens || 0), 10) || 0) > 0) challengers.push(i);
    }
  }
  return challengers;
}

function backupInterventionUI(ctx: BeatLineInterventionContext, targetP: number): boolean {
  const zone = document.getElementById(`timeline-p${targetP}`);
  if (!zone) return false;
  const td = document.getElementById('turn-display');
  ctx.state.__interventionBackup = {
    targetP,
    nodes: Array.from(zone.childNodes),
    turnText: td ? td.innerText : '',
    turnColor: td ? td.style.color : '',
  };
  return true;
}

export function restoreInterventionUIFromTypeScript(ctx: BeatLineInterventionContext): BeatLineInterventionResult {
  const state = ctx.state || {};
  const backup = state.__interventionBackup;
  if (!backup || !backup.nodes) return { handled: false, reason: 'missing-backup' };

  const zone = document.getElementById(`timeline-p${backup.targetP}`);
  if (zone) {
    zone.innerHTML = '';
    (backup.nodes as Node[]).forEach((node) => zone.appendChild(node));
  }

  const td = document.getElementById('turn-display');
  const t = getT(ctx);
  if (td) {
    td.innerText = backup.turnText || `${t('player', 'SPELER')} ${state.turn}`;
    td.style.color = backup.turnColor || '';
  }

  state.__interventionBackup = null;
  state.challenger = 0;
  safeCall(ctx.updateUI, true);
  return { handled: true, reason: 'restored' };
}

export function openInterventionDecisionFromTypeScript(ctx: BeatLineInterventionContext): BeatLineInterventionResult {
  if (!normalOfflineOnly(ctx)) return { handled: false, reason: 'complex-or-online' };
  const state = ctx.state;
  const challengers = Array.isArray(state.__interventionChallengers)
    ? state.__interventionChallengers
    : getOfflineChallengersFromTypeScript(ctx);
  const t = getT(ctx);
  const l = lang(ctx);
  const noText = typeof state.__interventionNoText === 'string'
    ? state.__interventionNoText
    : t('intervention_no_check', 'NEE (Gewoon Checken)');

  callShowDecision(ctx, {
    noText,
    yesText: l === 'en' ? 'YES' : 'JA',
    onNo: () => { void (window as any).resolveIntervention?.(0); },
    onYes: () => {
      if (!challengers.length) return;
      if ((parseInt(String(state.playerCount || 1), 10) || 1) >= 3) {
        callShowPick(ctx, {
          challengers,
          onPick: (pid: number) => { void (window as any).resolveIntervention?.(pid); },
          onBack: () => { openInterventionDecisionFromTypeScript(ctx); },
        });
      } else {
        void (window as any).resolveIntervention?.(challengers[0] || 0);
      }
    },
  });

  return { handled: true, reason: 'decision-opened' };
}

function pendingId(ctx: BeatLineInterventionContext): string {
  const state = ctx.state || {};
  const online = ctx.online || {};
  if (online.enabled && online.stateRef && state.tempActivePlacement && state.tempActivePlacement.activeId) {
    return String(state.tempActivePlacement.activeId || '');
  }
  if (online.enabled && typeof state.activeCardId !== 'undefined') return String(state.activeCardId || '');
  return String((state.tempActivePlacement && state.tempActivePlacement.activeId) || (state.activeCard && state.activeCard.id) || '');
}

function fallbackPendingCardHTML(id: string): string {
  const safeId = String(id || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  return `<div class="card" data-id="${safeId}"><div class="card-artist">BeatLine</div><div class="card-icon">🎵</div><div class="card-year"></div><div class="card-title">Luister...</div></div>`;
}

export function renderChallengeUIFromTypeScript(targetP: number, actorP: number, excludeIndex: number, ctx: BeatLineInterventionContext): BeatLineInterventionResult {
  if (!normalOfflineOnly(ctx)) return { handled: false, reason: 'complex-or-online' };
  const zone = document.getElementById(`timeline-p${targetP}`);
  if (!zone) return { handled: false, reason: 'missing-zone' };

  const t = getT(ctx);
  const tf = getTf(ctx);
  const state = ctx.state;
  zone.innerHTML = '';
  const timelineData = Array.isArray(player(ctx, targetP).timeline)
    ? player(ctx, targetP).timeline.slice().sort((a: Record<string, any>, b: Record<string, any>) => (a.year || 0) - (b.year || 0))
    : [];
  const pId = pendingId(ctx);

  const mkBtn = (idx: number) => {
    if (idx === excludeIndex) return;
    const btn = document.createElement('button');
    btn.className = 'place-btn challenging';
    btn.innerText = t('place_here', 'PLAATS HIER');
    btn.onclick = () => { void (window as any).checkChallenge?.(targetP, actorP, idx); };
    zone.appendChild(btn);
  };

  const len = timelineData.length;
  for (let pos = 0; pos <= len; pos += 1) {
    if (pos === excludeIndex && pId) {
      insertChallengePendingCard(zone as HTMLElement, pId, ctx);
    } else {
      mkBtn(pos);
    }
    if (pos < len) {
      if (typeof ctx.createCardHTML === 'function') zone.insertAdjacentHTML('beforeend', ctx.createCardHTML(timelineData[pos], true));
    }
  }

  const td = document.getElementById('turn-display');
  if (td) {
    td.innerText = tf('turn_player_choose_pos', { p: actorP });
    td.style.color = 'var(--token-gold)';
  }
  safeCall(ctx.updateUI, true);
  return { handled: true, reason: 'challenge-ui-rendered' };
}

export async function resolveInterventionFromTypeScript(challengerId: number, ctx: BeatLineInterventionContext): Promise<BeatLineInterventionResult> {
  if (!normalOfflineOnly(ctx)) return { handled: false, reason: 'complex-or-online' };
  const state = ctx.state;
  state.challenger = challengerId;
  safeCall(ctx.stopMusic);

  if (challengerId === 0) {
    callHideCenter(ctx);
    state.__interventionBackup = null;
    state.__interventionChallengers = null;
    state.__interventionNoText = null;

    const activeId = (state.tempActivePlacement && state.tempActivePlacement.activeId) ? state.tempActivePlacement.activeId : (state.activeCard ? state.activeCard.id : '');
    let meta = activeId && ctx.songMetaCache ? ctx.songMetaCache[activeId] : null;
    if (!meta && activeId && typeof ctx.fetchSongMeta === 'function') {
      await ctx.fetchSongMeta([activeId]);
      meta = ctx.songMetaCache ? (ctx.songMetaCache[activeId] || null) : null;
    }
    if (meta) {
      state.activeCard = meta;
      if (state.tempActivePlacement && state.tempActivePlacement.card) safeCall(ctx.revealCardElement, state.tempActivePlacement.card, meta);
    }
    safeCall(ctx.finalizeTurn, parseInt(String(state.turn || 1), 10) || 1, !!(state.tempActivePlacement && state.tempActivePlacement.valid));
    return { handled: true, reason: 'no-intervention' };
  }

  const targetP = parseInt(String(state.turn || 1), 10) || 1;
  backupInterventionUI(ctx, targetP);
  callShowActive(ctx, {
    challengerId,
    onBack: () => {
      restoreInterventionUIFromTypeScript(ctx);
      openInterventionDecisionFromTypeScript(ctx);
    },
  });
  return renderChallengeUIFromTypeScript(targetP, challengerId, state.tempActivePlacement ? state.tempActivePlacement.index : 0, ctx);
}

function buildPlaylistPayload(ctx: BeatLineInterventionContext): Record<string, any> {
  const state = ctx.state;
  const ids = state.playlistIds || [state.playlistId || ctx.currentPlaylistId];
  if (typeof ctx.beatlineBuildPlaylistParams === 'function') return ctx.beatlineBuildPlaylistParams(ids);
  return { playlist_ids: Array.isArray(ids) ? ids : [ids] };
}

export async function checkChallengeFromTypeScript(targetP: number, actorP: number, index: number, ctx: BeatLineInterventionContext): Promise<BeatLineInterventionResult> {
  if (!normalOfflineOnly(ctx)) return { handled: false, reason: 'complex-or-online' };
  if (typeof ctx.ajaxPost !== 'function') return { handled: false, reason: 'missing-ajax' };

  callHideCenter(ctx);
  const state = ctx.state;
  state.__interventionBackup = null;
  state.__interventionChallengers = null;
  state.__interventionNoText = null;

  const activeId = (state.tempActivePlacement && state.tempActivePlacement.activeId) ? state.tempActivePlacement.activeId : (state.activeCard ? state.activeCard.id : '');
  const activePrevId = (state.tempActivePlacement && state.tempActivePlacement.prevId) ? state.tempActivePlacement.prevId : '';
  const activeNextId = (state.tempActivePlacement && state.tempActivePlacement.nextId) ? state.tempActivePlacement.nextId : '';

  const timelineSorted = Array.isArray(player(ctx, targetP).timeline)
    ? player(ctx, targetP).timeline.slice().sort((a: Record<string, any>, b: Record<string, any>) => (a.year || 0) - (b.year || 0))
    : [];
  const challPrevId = index > 0 && timelineSorted[index - 1] ? timelineSorted[index - 1].id : '';
  const challNextId = index < timelineSorted.length && timelineSorted[index] ? timelineSorted[index].id : '';

  const resp = await ctx.ajaxPost('beatline_validate_challenge', Object.assign(buildPlaylistPayload(ctx), {
    mode: state.mode || 'spotify',
    active_id: activeId,
    active_prev_id: activePrevId,
    active_next_id: activeNextId,
    challenger_prev_id: challPrevId,
    challenger_next_id: challNextId,
  }));

  const activeWasCorrect = !!(resp && resp.success && resp.data && resp.data.active_valid);
  const challengerCorrect = !!(resp && resp.success && resp.data && resp.data.challenger_valid);
  const song = resp && resp.success && resp.data && resp.data.song ? resp.data.song : null;

  if (song && song.id) {
    if (ctx.songMetaCache) ctx.songMetaCache[song.id] = song;
    state.activeCard = song;
  }

  player(ctx, actorP).tokens = Math.max(0, (parseInt(String(player(ctx, actorP).tokens || 0), 10) || 0) - 1);
  const t = getT(ctx);

  if (activeWasCorrect) {
    player(ctx, targetP).timeline.push(state.activeCard);
    const fx = () => safeCall(ctx.confetti, { particleCount: 150, spread: 70, origin: { y: 0.6 }, zIndex: 9999999 });
    const text = typeof ctx.beatlineInterventionActiveCorrectText === 'function' ? ctx.beatlineInterventionActiveCorrectText(targetP, actorP) : '';
    safeCall(ctx.showBonusModal, t('correct', 'CORRECT!'), text, 'correct', { effectFn: fx, bonusPlayerId: targetP });
  } else if (challengerCorrect) {
    player(ctx, actorP).timeline.push(state.activeCard);
    const text = typeof ctx.beatlineInterventionStolenText === 'function' ? ctx.beatlineInterventionStolenText(actorP, targetP) : '';
    safeCall(ctx.showBonusModal, t('stolen_title', 'GESTOLEN!'), text, 'stolen', { effectFn: ctx.triggerStealEffect, bonusPlayerId: targetP });
  } else {
    const text = typeof ctx.beatlineInterventionBothWrongText === 'function' ? ctx.beatlineInterventionBothWrongText(actorP) : '';
    safeCall(ctx.showBonusModal, t('wrong', 'FOUT!'), text, 'wrong', { effectFn: ctx.triggerWrongEffect, bonusPlayerId: targetP });
  }

  return { handled: true, reason: 'challenge-checked' };
}
